import { startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CelebrationType } from 'src/lib/enum';
import { AffiliateRes } from 'src/lib/types';

import { Box, Stack, Typography } from '@mui/material';

import CaloLoader from '../../components/CaloLoader';
import FullScreenCelebration from '../../components/FullScreenCelebration';
import useTransactionCelebration from '../../hooks/useTransactionCelebration';
import { getUserProperties, identifyUser, trackAffiliatePortalViewed } from '../../lib/mixpanel';
import MetricsGrid from './components/MetricsGrid';
import PaymentProgress from './components/PaymentProgress';
import TransactionsTable from './components/TransactionsTable';
import AffiliateCouponsTable from './components/AffiliateCouponsTable';
import useAffiliateProfile from './useAffiliateProfile';
import useAffiliateCreditTransactions from './useAffiliateCreditTransactions';
import useAffiliateDebitTransactions from './useAffiliateDebitTransactions';

interface TransactionsListProps {
  userId: string;
}

const TransactionsCard = ({ userId }: TransactionsListProps) => {
  const [creditPageNumber, setCreditPageNumber] = useState(1);
  const [debitPageNumber, setDebitPageNumber] = useState(1);
  const perPage = 10;

  const { profile, profileLoading, affiliateCoupons } = useAffiliateProfile({
    affiliateId: userId
  });

  const {
    transactions: creditTransactions,
    pagination: creditPagination,
    transactionsLoading: creditLoading
  } = useAffiliateCreditTransactions({
    pageNumber: creditPageNumber,
    perPage
  });

  const {
    transactions: debitTransactions,
    pagination: debitPagination,
    transactionsLoading: debitLoading
  } = useAffiliateDebitTransactions({
    pageNumber: debitPageNumber,
    perPage
  });

  // Create a temporary affiliate object for calculateEarnings if profile exists
  const affiliateForCalculations = useMemo((): AffiliateRes | undefined => {
    if (!profile) return;
    
    return {
      id: profile.id,
      name: profile.name,
      dateJoined: profile.createdAt,
      codeName: profile.codeName,
      totalUsersAttracted: profile.totalUsersInvited,
      email: profile.email,
      phoneNumber: profile.phoneNumber,
      country: profile.country,
      currency: profile.currency
    } as AffiliateRes;
  }, [profile]);

  const handlePageChange = (pageNumber: number, isCredit: boolean) => {
    if (isCredit) {
      setCreditPageNumber(pageNumber);
    } else {
      setDebitPageNumber(pageNumber);
    }
  };

  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationInfo, setCelebrationInfo] = useState<{
    currency: string;
    transactionCount?: number;
    totalAmount?: string;
  } | null>(null);

  const handleCelebrate = useCallback((displayCurrency, totalInfo) => {
    setCelebrationInfo({
      currency: displayCurrency,
      transactionCount: totalInfo?.count,
      totalAmount: totalInfo?.totalAmount
    });
    setShowCelebration(true);
  }, []);

  // Combine transactions for celebration hook (using debit transactions for now)
  // Note: useTransactionCelebration may need to be updated to work with new transaction structure
  // For now, passing empty array to avoid type errors
  useTransactionCelebration({
    transactions: [],
    affiliateCurrency: profile?.currency,
    onCelebrate: handleCelebrate
  });

  const handleCloseCelebration = () => {
    setShowCelebration(false);
    setCelebrationInfo(null);
  };

  useEffect(() => {
    if (profile && !profileLoading && affiliateForCalculations) {
      identifyUser(userId, getUserProperties(affiliateForCalculations));
      trackAffiliatePortalViewed(userId, affiliateForCalculations);
    }
  }, [profile, profileLoading, userId, affiliateForCalculations]);

  if (profileLoading) {
    return (
      <Box
        sx={{
          border: 'none',
          borderRadius: '8px',
          margin: { xs: '12px', sm: '24px' },
          width: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}
      >
        <CaloLoader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: 'none',
        borderRadius: '8px',
        margin: { xs: '12px', sm: '24px' },
        width: 'auto'
      }}
    >
      {showCelebration && celebrationInfo && (
        <FullScreenCelebration
          onClose={handleCloseCelebration}
          transactionCount={celebrationInfo.transactionCount}
          totalAmount={celebrationInfo.totalAmount}
          affiliateId={userId}
          affiliate={affiliateForCalculations}
          type={CelebrationType.REFERRAL}
        />
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="stretch"
        gap={4}
        sx={{
          border: 'none',
          borderRadius: '8px',
          minHeight: { xs: 'auto', md: '250px' }
        }}
      >
        <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" gap={3} flex={1}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 500,
              color: '#111827',
              fontSize: { xs: '24px', sm: '30px' }
            }}
          >
            Hey {startCase(profile?.name || '')} 👋
          </Typography>
          <MetricsGrid
            totalUsersAttracted={profile?.totalUsersInvited || 0}
            lifetimeEarnings={profile?.totalEarned || 0}
            currency={profile?.currency}
            balance={profile?.balance}
          />
        </Stack>
        <PaymentProgress />
      </Stack>

      <AffiliateCouponsTable
        coupons={affiliateCoupons}
        couponsLoading={profileLoading}
        currency={profile?.currency}
        affiliateId={userId}
        affiliate={affiliateForCalculations}
      />
      <TransactionsTable
        creditTransactions={creditTransactions}
        debitTransactions={debitTransactions}
        creditLoading={creditLoading}
        debitLoading={debitLoading}
        creditPagination={creditPagination}
        debitPagination={debitPagination}
        affiliateCurrency={profile?.currency}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default TransactionsCard; 